// Assistant « Mesure 0 » — état des lieux, questionnaire (facultatif),
// fonctionnalités, utilisateurs, bilan (ce qui fonctionne ou non), piliers
// d'adoption. Sur un projet neuf, il s'ouvre sur un mot d'accueil qui montre le
// chemin : mesurer, comprendre, décider, prouver.
//
// Deux présentations pour les mêmes étapes : en PANNEAU latéral (modifier la
// mesure d'un projet ouvert) et en PLEINE PAGE (`page`, le parcours de démarrage
// d'un projet vide — voir `KickoffPage`), où chaque étape porte un grand titre.
//
// `only` réduit l'assistant à UNE étape, sans fil d'étapes : le parcours de
// démarrage ne demande que l'état des lieux, et chaque carte « à compléter » du
// tableau de bord (`ProjectTodo`) ouvre la sienne. Valider ferme l'assistant.
//
// Ce que l'on saisit ici est ce que le CLIENT déclare de son produit. Ce n'est
// pas une preuve : c'est la référence que les études viendront confronter.
//
// Chaque étape s'enregistre à sa validation, et n'enregistre QUE sa section : le
// serveur traite une section absente comme « inchangée ». Revenir en arrière ne
// perd donc rien, et fermer l'assistant à mi-parcours non plus (ce qui est
// validé reste). Seules les modifications de l'étape en cours, non validées,
// ne survivent pas à la fermeture.
import { useEffect, useId, useRef, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import {
  useAnalyzeProductUrl,
  useDeletePillar,
  useSaveProductContext,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type {
  ContextProposal, FeatureStatus, Phase, PillarModel, ProductContext, ProductMarket, ProductType,
} from '../api/types';
import { Button, ErrorBanner, IconButton, Note, Select, VoiceInput } from '../components/ui';
import { PHASE_LABELS } from '../lifecycle/ProjectFilter';
import { Mesure0Questionnaire } from './Mesure0Questionnaire';
import { Icon, type IconName } from '../components/icons';
import type { Hue } from '../taxonomy/colors';
import { JourneyIllustration } from '../kickoff/JourneyIllustration';
import './mesure0.css';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

type RequiredStep = 'product' | 'features' | 'roles' | 'pillars';

/** Les étapes de l'assistant. `key: null` = étape facultative, qui ne compte
 *  pas dans l'avancement. */
export type StepId = RequiredStep | 'questionnaire' | 'diagnosis';
/** `title` : la question posée au directeur, en tête d'étape (pleine page). */
const STEPS: { id: StepId; label: string; title: string; key: RequiredStep | null }[] = [
  { id: 'product', label: 'État des lieux', title: 'Où en est votre produit ?', key: 'product' },
  { id: 'questionnaire', label: 'Questionnaire', title: 'Dix questions pour cadrer le besoin', key: null },
  { id: 'features', label: 'Fonctionnalités', title: 'Que permet le produit ?', key: 'features' },
  { id: 'roles', label: 'Utilisateurs', title: 'Qui utilise le produit ?', key: 'roles' },
  { id: 'diagnosis', label: 'Bilan', title: 'Qu’est-ce qui fonctionne, et qu’est-ce qui ne fonctionne pas ?', key: null },
  { id: 'pillars', label: 'Piliers', title: 'Que doit réussir le produit pour être adopté ?', key: 'pillars' },
];
const LAST = STEPS.length - 1;

/** Libellés des étapes OBLIGATOIRES, dans l'ordre — pour le bloc du tableau de bord. */
export const REQUIRED_STEPS = STEPS.filter((s) => s.key !== null) as { label: string; key: RequiredStep }[];

// Une phrase par phase : le directeur n'a pas à connaître le vocabulaire produit.
const PHASE_HINTS: Record<Phase, string> = {
  creation: 'le produit n’existe pas encore, c’est une idée',
  discovery: 'on explore le besoin avant de construire',
  delivery: 'le produit est en cours de construction',
  run: 'le produit est en production et utilisé',
  refonte: 'un produit existant que l’on repense',
};

const TYPE_LABELS: Record<ProductType, string> = {
  logiciel_metier: 'Logiciel métier',
  saas: 'SaaS',
  app_mobile: 'Application mobile',
  site_web: 'Site web',
  ecommerce: 'Site e-commerce',
  autre: 'Autre',
};

const MARKET_LABELS: Record<ProductMarket, string> = {
  b2b: 'B2B — vendu à des entreprises',
  b2c: 'B2C — vendu au grand public',
  b2b2c: 'B2B2C — vendu à des entreprises, pour leurs clients',
  interne: 'B2E — outil interne, utilisé par vos propres équipes',
};

const STATUS_LABELS: Record<FeatureStatus, string> = {
  en_production: 'En production',
  en_cours: 'En cours',
  prevue: 'Prévue',
};

// Les lignes d'une liste éditable ont besoin d'une clé STABLE : le nom, lui, est
// en cours de frappe, et l'identifiant serveur n'existe pas encore pour une
// ligne neuve. Sans clé propre, React recréerait le champ à chaque caractère.
interface FeatureRow { key: number; id: string | undefined; name: string; description: string; status: FeatureStatus }
interface RoleRow { key: number; id: string | undefined; name: string; headcount: string; usage: string }
interface PointRow { key: number; text: string }

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

export function Mesure0Wizard({
  projectId,
  projectName,
  context,
  welcome = false,
  page = false,
  only,
  onClose,
}: {
  projectId: string;
  projectName: string;
  context: ProductContext;
  /** Projet neuf : on commence par montrer le chemin. */
  welcome?: boolean;
  /** Présentation pleine page (parcours de démarrage) plutôt qu'en panneau. */
  page?: boolean;
  /** Une seule étape : pas de fil d'étapes, et valider ferme l'assistant. */
  only?: StepId;
  onClose: () => void;
}) {
  const single = only !== undefined;
  const [intro, setIntro] = useState(welcome);
  const rootRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const nextKey = () => (seq.current += 1);

  const [step, setStep] = useState(() => {
    if (only) return STEPS.findIndex((s) => s.id === only);
    // On reprend là où l'on s'était arrêté : première étape OBLIGATOIRE non franchie.
    const i = STEPS.findIndex((s) => s.key !== null && !context.steps[s.key]);
    return i === -1 ? 0 : i;
  });

  const [description, setDescription] = useState(context.product.description ?? '');
  const [version, setVersion] = useState(context.product.version ?? '');
  const [url, setUrl] = useState(context.product.url ?? '');
  const [sectors, setSectors] = useState(context.product.sectors.join(', '));
  const [phase, setPhase] = useState<Phase>(context.product.phase ?? 'discovery');
  const [type, setType] = useState<ProductType | ''>(context.product.type ?? '');
  const [market, setMarket] = useState<ProductMarket | ''>(context.product.market ?? '');
  // Plus demandés à l'état des lieux (allégé à trois questions), mais renvoyés
  // tels quels à l'enregistrement : rien de déjà saisi ne s'efface.
  const [objective] = useState(context.product.objective ?? '');
  const [works, setWorks] = useState<PointRow[]>(() => context.product.works.map((text) => ({ key: nextKey(), text })));
  const [fails, setFails] = useState<PointRow[]>(() => context.product.fails.map((text) => ({ key: nextKey(), text })));
  const [features, setFeatures] = useState<FeatureRow[]>(() =>
    context.features.map((f) => ({
      key: nextKey(), id: f.id, name: f.name, description: f.description ?? '', status: f.status,
    })),
  );
  const [roles, setRoles] = useState<RoleRow[]>(() =>
    context.roles.map((r) => ({
      key: nextKey(), id: r.id, name: r.name,
      headcount: r.headcount === null ? '' : String(r.headcount),
      usage: r.usage ?? '',
    })),
  );
  // Ligne ajoutée à la main : son premier champ reçoit le curseur. Sans cela, on
  // tape après « + Ajouter » et le texte part dans le vide (le focus est resté
  // sur le bouton).
  const [focusKey, setFocusKey] = useState<number | null>(null);
  function addFeature() {
    const key = nextKey();
    setFocusKey(key);
    setFeatures((rows) => [...rows, { key, id: undefined, name: '', description: '', status: 'en_production' }]);
  }
  function addRole() {
    const key = nextKey();
    setFocusKey(key);
    setRoles((rows) => [...rows, { key, id: undefined, name: '', headcount: '', usage: '' }]);
  }
  function addPoint(set: Dispatch<SetStateAction<PointRow[]>>) {
    const key = nextKey();
    setFocusKey(key);
    set((rows) => [...rows, { key, text: '' }]);
  }

  const save = useSaveProductContext(projectId);
  const analyze = useAnalyzeProductUrl(projectId);

  // Pleine page : une nouvelle étape se lit depuis son titre, pas depuis
  // l'endroit où l'on avait défilé pour valider la précédente.
  useEffect(() => {
    if (page) rootRef.current?.closest('.kickoff')?.scrollTo(0, 0);
  }, [page, step, intro]);

  // --- Lecture du site : une PROPOSITION, fusionnée sans écraser la saisie ----
  function applyProposal(p: ContextProposal) {
    setDescription((d) => d || p.product.description || '');
    setVersion((v) => v || p.product.version || '');
    setSectors((s) => s || p.product.sectors.join(', '));
    if (p.product.url) setUrl(p.product.url);
    const has = (rows: { name: string }[], name: string) =>
      rows.some((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase());
    setFeatures((rows) => [
      ...rows,
      ...p.features.filter((f) => !has(rows, f.name)).map((f) => ({
        key: nextKey(), id: undefined, name: f.name, description: f.description ?? '', status: f.status,
      })),
    ]);
    setRoles((rows) => [
      ...rows,
      ...p.roles.filter((r) => !has(rows, r.name)).map((r) => ({
        key: nextKey(), id: undefined, name: r.name,
        headcount: r.headcount === null ? '' : String(r.headcount), usage: r.usage ?? '',
      })),
    ]);
  }

  async function runAnalysis() {
    try {
      const res = await analyze.mutateAsync(url.trim());
      applyProposal(res.proposal);
    } catch {
      /* l'erreur est rendue sous le champ */
    }
  }

  // --- Type et modèle : le type préremplit le marché, qui reste à confirmer --
  function chooseType(v: string) {
    const next = v as ProductType | '';
    setType(next);
    const suggested = next ? context.catalog.typeMarkets[next] : undefined;
    if (suggested && !market) setMarket(suggested);
  }
  const model = market ? context.catalog.models[context.catalog.marketModels[market]] : null;

  // --- Métiers : nombre d'utilisateurs --------------------------------------
  const namedRoles = roles.filter((r) => r.name.trim());
  const totalUsers = namedRoles.reduce((n, r) => {
    const v = num(r.headcount);
    return v !== null && Number.isFinite(v) && v > 0 ? n + v : n;
  }, 0);

  // --- Enregistrement de l'étape courante, puis passage à la suivante --------
  async function submit(e: FormEvent) {
    e.preventDefault();
    const id = STEPS[step]?.id;
    try {
      if (id === 'product') {
        await save.mutateAsync({
          product: {
            phase,
            type: type || null,
            market: market || null,
            objective: objective.trim(),
            description: description.trim(),
            version: version.trim(),
            url: url.trim(),
            sectors: sectors.split(',').map((s) => s.trim()).filter(Boolean),
          },
        });
      } else if (id === 'diagnosis') {
        const points = (rows: PointRow[]) => rows.map((r) => r.text.trim()).filter(Boolean);
        await save.mutateAsync({ product: { works: points(works), fails: points(fails) } });
      } else if (id === 'features') {
        await save.mutateAsync({
          features: features.filter((f) => f.name.trim()).map((f) => ({
            ...(f.id ? { id: f.id } : {}),
            name: f.name.trim(), description: f.description.trim(), status: f.status,
          })),
        });
      } else if (id === 'roles') {
        await save.mutateAsync({
          roles: namedRoles.map((r) => ({
            ...(r.id ? { id: r.id } : {}),
            name: r.name.trim(),
            headcount: num(r.headcount),
            // La part d'usage n'est plus saisie (abandonnée : trop lourde en
            // entretien). On l'efface explicitement plutôt que de laisser en base
            // une répartition que plus personne ne peut voir ni corriger.
            usageShare: null,
            usage: r.usage.trim(),
          })),
        });
      }
      if (single) onClose();
      else setStep((s) => Math.min(LAST, s + 1));
    } catch {
      /* l'erreur est rendue en bas de l'étape */
    }
  }

  const id = STEPS[step]?.id ?? null;
  const canContinue =
    id === 'product' ? Boolean(type && market)
      : true;
  const hasDiagnosis = context.product.works.length + context.product.fails.length > 0;

  if (intro) return <Welcome projectName={projectName} page={page} short={single} onStart={() => setIntro(false)} />;

  return (
    <div ref={rootRef} className={`m0-wizard${page ? ' m0-wizard-page' : ''}`}>
      {single ? null : (
      <ol className="m0-steps" aria-label="Étapes de la mesure zéro">
        {STEPS.map((s, i) => {
          const done = s.key !== null ? context.steps[s.key] : s.id === 'diagnosis' && hasDiagnosis;
          return (
            <li key={s.label}>
              <button
                type="button"
                className={`m0-step${i === step ? ' m0-step-on' : ''}${done ? ' m0-step-done' : ''}`}
                aria-current={i === step ? 'step' : undefined}
                onClick={() => setStep(i)}
              >
                <span className="m0-step-num">{done && i !== step ? '✓' : i + 1}</span>
                <span>{s.label}</span>
                {s.key === null ? <span className="m0-step-opt">facultatif</span> : null}
              </button>
            </li>
          );
        })}
      </ol>
      )}

      {page ? (
        <header className="kick-step">
          <div>
            <p className="t-mono kick-count">
              {single ? (
                STEPS[step]?.label
              ) : (
                <>
                  Étape {step + 1} sur {STEPS.length}
                  {STEPS[step]?.key === null ? ' · facultative' : ''}
                </>
              )}
            </p>
            <h1 className="t-h1">{STEPS[step]?.title}</h1>
          </div>
          {/* L'état des lieux validé, le projet peut s'ouvrir : le reste se
              reprend depuis le tableau de bord. */}
          {!single && context.steps.product && step < LAST ? (
            <Button type="button" variant="ghost" onClick={onClose}>
              Terminer plus tard
            </Button>
          ) : null}
        </header>
      ) : null}

      {id === 'questionnaire' ? (
        <>
          <Mesure0Questionnaire projectId={projectId} projectName={projectName} />
          <div className="m0-actions">
            {single ? (
              <Button type="button" variant="ghost" onClick={onClose}>
                Fermer
              </Button>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)}>
                  Retour
                </Button>
                <Button type="button" variant="accent" onClick={() => setStep((s) => s + 1)}>
                  Continuer
                </Button>
              </>
            )}
          </div>
        </>
      ) : step < LAST ? (
        <form className="write-form" onSubmit={submit} noValidate>
          {id === 'product' ? (
            <>
              <p className="m0-note">
                Trois questions, pour poser votre vision du produit. Les études viendront ensuite la
                confronter au terrain.
              </p>
              <SelectField
                label="Où en est le produit ? *"
                value={phase}
                onChange={(v) => setPhase(v as Phase)}
                options={(Object.keys(PHASE_LABELS) as Phase[]).map((p) => ({
                  value: p, label: `${PHASE_LABELS[p]} — ${PHASE_HINTS[p]}`,
                }))}
              />
              <SelectField
                label="Type de produit *"
                value={type}
                onChange={chooseType}
                empty="À préciser"
                options={(Object.keys(TYPE_LABELS) as ProductType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] }))}
              />
              <SelectField
                label="À qui s’adresse-t-il ? *"
                value={market}
                onChange={(v) => setMarket(v as ProductMarket | '')}
                empty="À préciser"
                options={(Object.keys(MARKET_LABELS) as ProductMarket[]).map((m) => ({ value: m, label: MARKET_LABELS[m] }))}
              />
              {model ? <ModelPreview model={model} locked={context.product.modelLocked} /> : null}
            </>
          ) : null}

          {id === 'features' ? (
            <>
              <p className="m0-note">
                Les fonctionnalités que le client met en avant. Chacune pourra ensuite être évaluée par
                les études. Le micro de chaque champ permet de les dicter pendant l’entretien.
              </p>
              <ul className="m0-rows">
                {features.map((f, i) => (
                  <li key={f.key} className="m0-row">
                    <div className="m0-row-main">
                      <div className="m0-voice">
                        <VoiceInput
                          aria-label={`Nom de la fonctionnalité ${i + 1}`}
                          autoFocus={f.key === focusKey}
                          value={f.name}
                          placeholder="Gestion des stocks"
                          onChange={(e) => setFeatures((rows) => rows.map((r) => (r.key === f.key ? { ...r, name: e.target.value } : r)))}
                        />
                      </div>
                      <Select
                        className="m0-select"
                        aria-label={`Statut de la fonctionnalité ${i + 1}`}
                        value={f.status}
                        onChange={(e) => setFeatures((rows) => rows.map((r) => (r.key === f.key ? { ...r, status: e.target.value as FeatureStatus } : r)))}
                      >
                        {(Object.keys(STATUS_LABELS) as FeatureStatus[]).map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </Select>
                      <IconButton icon="close" danger
                        label={`Retirer la fonctionnalité ${f.name || i + 1}`}
                        onClick={() => setFeatures((rows) => rows.filter((r) => r.key !== f.key))} />
                    </div>
                    <VoiceInput
                      aria-label={`Description de la fonctionnalité ${i + 1}`}
                      value={f.description}
                      placeholder="Ce qu’elle permet (facultatif)"
                      onChange={(e) => setFeatures((rows) => rows.map((r) => (r.key === f.key ? { ...r, description: e.target.value } : r)))}
                    />
                  </li>
                ))}
              </ul>
              <div className="m0-actions m0-actions-left">
                <Button type="button" variant="ghost" onClick={addFeature}>
                  + Ajouter une fonctionnalité
                </Button>
                <SiteProposal single={single} url={url} pending={analyze.isPending} onRun={() => void runAnalysis()} />
              </div>
              {analyze.error ? <ErrorBanner>{errorMessage(analyze.error)}</ErrorBanner> : null}
            </>
          ) : null}

          {id === 'diagnosis' ? (
            <>
              <p className="m0-note">
                Selon vous, qu’est-ce qui <strong>fonctionne</strong> et qu’est-ce qui <strong>ne fonctionne
                pas</strong> dans le produit ? C’est votre ressenti, à titre indicatif : rien ici n’est une
                preuve. Ces points aident l’expert à définir des pistes d’exploration et les problèmes à
                creuser pendant la recherche utilisateur. Un point par ligne, en quelques mots.
              </p>
              <PointList
                title="Ce qui fonctionne"
                rows={works}
                setRows={setWorks}
                focusKey={focusKey}
                placeholder="Le scan des outils est rapide"
                addLabel="+ Ajouter un point fort"
                onAdd={() => addPoint(setWorks)}
              />
              <PointList
                title="Ce qui ne fonctionne pas"
                rows={fails}
                setRows={setFails}
                focusKey={focusKey}
                placeholder="Les chefs de chantier n’ouvrent pas l’application"
                addLabel="+ Ajouter un problème"
                onAdd={() => addPoint(setFails)}
              />
            </>
          ) : null}

          {id === 'roles' ? (
            <>
              <p className="m0-note">
                Les métiers ou rôles qui utilisent le produit, selon le client, et leur <strong>nombre
                d’utilisateurs</strong> approximatif. Ce sont des hypothèses : vos études les confirmeront
                ou les corrigeront. Chaque champ se dicte au micro.
              </p>
              <div className="m0-role-head" aria-hidden="true">
                <span>Métier ou rôle</span>
                <span>Nb utilisateurs</span>
                <span />
              </div>
              <ul className="m0-rows">
                {roles.map((r, i) => (
                  <li key={r.key} className="m0-row">
                    <div className="m0-row-main">
                      <div className="m0-voice">
                        <VoiceInput
                          aria-label={`Métier ou rôle ${i + 1}`}
                          autoFocus={r.key === focusKey}
                          value={r.name}
                          placeholder="Magasinier"
                          onChange={(e) => setRoles((rows) => rows.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))}
                        />
                      </div>
                      <input
                        className="zy-input m0-count"
                        type="number"
                        min={0}
                        inputMode="numeric"
                        aria-label={`Nombre d’utilisateurs du métier ${i + 1}`}
                        value={r.headcount}
                        placeholder="Nb"
                        onChange={(e) => setRoles((rows) => rows.map((x) => (x.key === r.key ? { ...x, headcount: e.target.value } : x)))}
                      />
                      <IconButton icon="close" danger
                        label={`Retirer le métier ${r.name || i + 1}`}
                        onClick={() => setRoles((rows) => rows.filter((x) => x.key !== r.key))} />
                    </div>
                    <VoiceInput
                      aria-label={`Usage principal du métier ou rôle ${i + 1}`}
                      value={r.usage}
                      placeholder="Ce qu’il fait avec le produit, à quelle fréquence (facultatif)"
                      onChange={(e) => setRoles((rows) => rows.map((x) => (x.key === r.key ? { ...x, usage: e.target.value } : x)))}
                    />
                  </li>
                ))}
              </ul>
              <div className="m0-share-bar">
                <Button type="button" variant="ghost" onClick={addRole}>
                  + Ajouter un métier ou rôle
                </Button>
                <SiteProposal single={single} url={url} pending={analyze.isPending} onRun={() => void runAnalysis()} />
                {totalUsers > 0 ? (
                  <span className="m0-total" role="status">
                    {totalUsers.toLocaleString('fr-FR')} utilisateur{totalUsers > 1 ? 's' : ''} au total
                  </span>
                ) : null}
              </div>
              {analyze.error ? <ErrorBanner>{errorMessage(analyze.error)}</ErrorBanner> : null}
            </>
          ) : null}

          {save.error ? <ErrorBanner>{errorMessage(save.error)}</ErrorBanner> : null}
          <div className="m0-actions">
            {step > 0 && !single ? (
              <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)}>
                Retour
              </Button>
            ) : null}
            <Button type="submit" variant="accent" loading={save.isPending} disabled={!canContinue}>
              {single && !page ? 'Enregistrer' : 'Enregistrer et continuer'}
            </Button>
          </div>
        </form>
      ) : (
        <PillarsStep
          projectId={projectId}
          context={context}
          onBack={single ? undefined : () => setStep(LAST - 1)}
          onDone={onClose}
        />
      )}
    </div>
  );
}

// --- Accueil d'un projet neuf : le parcours, de la mesure à l'effet prouvé ------

/** Les cinq temps de la mesure dans Zygosight. Le premier, c'est maintenant. */
const JOURNEY: { icon: IconName; hue: Hue; title: string; text: string }[] = [
  { icon: 'eye', hue: 'cyan', title: 'Décrire le produit', text: 'Ce qu’il vise, à qui il s’adresse, comment il est vendu ou déployé. C’est l’état des lieux : il choisit vos piliers d’adoption. Et c’est maintenant.' },
  { icon: 'checklist', hue: 'violet', title: 'Mesurer', text: 'Un baromètre auprès de vos utilisateurs (NPS, SUS, questions par pilier), avec vos chiffres d’usage à côté.' },
  { icon: 'users', hue: 'orange', title: 'Comprendre pourquoi', text: 'Entretiens, observations, tests : ce que les chiffres ne disent pas, raconté par celles et ceux qui utilisent le produit.' },
  { icon: 'bulb', hue: 'amber', title: 'Décider', text: 'Ce qui revient, preuves à l’appui : ce qui bloque, ce qui aide, ce qu’il faut changer.' },
  { icon: 'gauge', hue: 'green', title: 'Prouver l’effet', text: 'Une nouvelle mesure dit si la correction a marché, pilier par pilier.' },
];

function Welcome({
  projectName, page, short, onStart,
}: {
  projectName: string;
  page: boolean;
  /** L'assistant ne demande que l'état des lieux : le reste se fait après. */
  short: boolean;
  onStart: () => void;
}) {
  const text = (
    <>
      {page ? (
        <>
          <p className="t-mono kick-count">Démarrer la mesure</p>
          <h1 className="t-h1">D’abord mesurer, ensuite comprendre</h1>
        </>
      ) : (
        <h3 className="t-h3">D’abord mesurer, ensuite comprendre</h3>
      )}
      <p>
        Mesurer <strong>{projectName}</strong>, c’est savoir où il en est et ce qui le fait bouger. Voici le
        chemin, étape par étape :
      </p>
      <ol className="m0-journey">
        {JOURNEY.map((j, i) => (
          <li key={j.title} className={i === 0 ? 'is-now' : undefined}>
            <span className={`m0-journey-icon hue-${j.hue}`}>
              <Icon name={j.icon} size={18} />
            </span>
            <span>
              <strong>
                {i + 1}. {j.title}
                {i === 0 ? <span className="m0-journey-now">Vous êtes ici</span> : null}
              </strong>
              <span>{j.text}</span>
            </span>
          </li>
        ))}
      </ol>
      <p>
        {short
          ? 'On commence par votre vision du produit. Comptez deux minutes ; le reste se complète ensuite depuis le tableau de bord, quand vous le souhaitez.'
          : 'On commence par votre vision du produit. Comptez une dizaine de minutes ; chaque étape validée est enregistrée, vous pouvez vous arrêter à tout moment.'}
      </p>
      <div className="m0-actions">
        <Button type="button" variant="accent" onClick={onStart}>
          Commencer l’état des lieux
        </Button>
      </div>
    </>
  );

  // Pleine page : le texte à gauche, le parcours dessiné à droite.
  if (page) {
    return (
      <div className="m0-welcome m0-welcome-split">
        <div className="write-form m0-welcome-text">{text}</div>
        <div className="m0-welcome-art">
          <JourneyIllustration />
        </div>
      </div>
    );
  }
  return <div className="write-form m0-welcome">{text}</div>;
}

// --- Champs de l'assistant ---------------------------------------------------

/** Étape ouverte seule, site renseigné : l'IA relit le site pour proposer la
 *  liste (fonctionnalités ou métiers). En parcours complet, c'est l'état des
 *  lieux qui l'a déjà pré-remplie. */
function SiteProposal({
  single, url, pending, onRun,
}: {
  single: boolean;
  url: string;
  pending: boolean;
  onRun: () => void;
}) {
  if (!single || !url.trim()) return null;
  return (
    <Button type="button" variant="ghost" loading={pending} onClick={onRun}>
      Proposer à partir du site
    </Button>
  );
}

function SelectField({
  label, value, onChange, options, empty,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  /** Libellé du choix « non renseigné ». Absent : un choix est obligatoire. */
  empty?: string;
}) {
  const id = useId();
  return (
    <div className="zy-field">
      <label className="zy-field__label" htmlFor={id}>{label}</label>
      <Select id={id} className="m0-select-wide" value={value} onChange={(e) => onChange(e.target.value)}>
        {empty ? <option value="">{empty}</option> : null}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    </div>
  );
}

/** Les piliers que le modèle choisi va poser : on les voit avant de valider. */
function ModelPreview({ model, locked }: { model: PillarModel; locked: boolean }) {
  return (
    <Note>
      <p>
        <strong>Modèle {model.label}.</strong> {model.why}
      </p>
      <ul className="m0-model-pillars">
        {model.pillars.map((p) => (
          <li key={p.key}>
            <strong>{p.name}</strong> : {p.measures.join(', ')}
          </li>
        ))}
      </ul>
      {model.excluded.length > 0 ? (
        <p>{model.excluded.join(' et ')} restent affichés, mais hors score : ils ne disent rien d’un outil imposé.</p>
      ) : null}
      {locked ? <p>Ce modèle est figé : des réponses au baromètre existent déjà.</p> : null}
    </Note>
  );
}

/** Liste de points courts, un par ligne (ce qui fonctionne / ne fonctionne pas). */
function PointList({
  title, rows, setRows, focusKey, placeholder, addLabel, onAdd,
}: {
  title: string;
  rows: PointRow[];
  setRows: Dispatch<SetStateAction<PointRow[]>>;
  focusKey: number | null;
  placeholder: string;
  addLabel: string;
  onAdd: () => void;
}) {
  return (
    <div className="m0-points">
      <p className="zy-field__label">{title}</p>
      {rows.length > 0 ? (
        <ul className="m0-rows">
          {rows.map((r, i) => (
            <li key={r.key} className="m0-row-main">
              <div className="m0-voice">
                <VoiceInput
                  aria-label={`${title}, point ${i + 1}`}
                  autoFocus={r.key === focusKey}
                  value={r.text}
                  placeholder={placeholder}
                  onChange={(e) => setRows((list) => list.map((x) => (x.key === r.key ? { ...x, text: e.target.value } : x)))}
                />
              </div>
              <IconButton icon="close" danger
                label={`Retirer le point ${r.text || i + 1}`}
                onClick={() => setRows((list) => list.filter((x) => x.key !== r.key))} />
            </li>
          ))}
        </ul>
      ) : null}
      <Button type="button" variant="ghost" onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  );
}

// --- Dernière étape : piliers d'adoption et notes déclarées ------------------

function PillarsStep({
  projectId,
  context,
  onBack,
  onDone,
}: {
  projectId: string;
  context: ProductContext;
  /** Absent : l'étape est ouverte seule, il n'y a pas d'étape précédente. */
  onBack: (() => void) | undefined;
  onDone: () => void;
}) {
  const remove = useDeletePillar(projectId);
  const save = useSaveProductContext(projectId);
  // Notes en cours de saisie, par pilier. Absente = la note enregistrée.
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Le serveur sert les piliers du modèle d'abord, puis les anciens.
  const pillars = context.pillars;
  const error = remove.error ?? save.error;
  const noteOf = (id: string, stored: number | null) => notes[id] ?? (stored === null ? '' : String(stored));
  const dirty = Object.keys(notes).length > 0;

  async function saveNotes(then?: () => void) {
    if (!dirty) { then?.(); return; }
    try {
      await save.mutateAsync({
        pillarScores: Object.entries(notes).map(([id, v]) => ({ id, declaredScore: num(v) })),
      });
      setNotes({});
      then?.();
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <div className="write-form">
      <p className="m0-note">
        Un pilier d’adoption est un <strong>objectif</strong> que le produit doit réussir pour que ses
        utilisateurs l’adoptent. Ils découlent de votre <strong>modèle</strong>, choisi à l’état des lieux.
        Notez chaque pilier <strong>comme le client le perçoit</strong>, de 0 à 10 : c’est ce qui permet
        de calculer l’Adoption Score déclaré.
      </p>

      {pillars.length > 0 ? (
        <ul className="m0-pillars">
          {pillars.map((p) => (
            <li key={p.id} className="m0-pillar">
              <div className="m0-pillar-body">
                <p className="m0-pillar-name">
                  {p.name}
                  {p.is_core ? null : <span className="m0-origin">Ancien</span>}
                </p>
                {p.objective ? <p className="m0-pillar-obj">{p.objective}</p> : null}
              </div>
              <label className="m0-note-input">
                <input
                  className="zy-input"
                  aria-label={`Note déclarée pour ${p.name}, sur 10`}
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  inputMode="decimal"
                  value={noteOf(p.id, p.declared_score)}
                  placeholder="—"
                  onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                />
                <span aria-hidden="true">/10</span>
              </label>
              {p.is_core ? (
                // Un pilier du modèle ne se retire pas : on tient juste la place du bouton.
                <span className="m0-keep" aria-hidden="true" />
              ) : (
                <IconButton icon="close" danger
                  label={`Retirer le pilier ${p.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(p.id)} />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="zy-texte-muet">Choisissez d’abord le type de produit et à qui il s’adresse, dans l’état des lieux.</p>
      )}
      {pillars.length > 0 ? (
        <Button type="button" variant="ghost" loading={save.isPending} disabled={!dirty} onClick={() => void saveNotes()}>
          {dirty ? 'Enregistrer les notes' : `${context.declaredScored}/${pillars.length} pilier${pillars.length > 1 ? 's' : ''} noté${pillars.length > 1 ? 's' : ''}`}
        </Button>
      ) : null}

      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
      <div className="m0-actions">
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            Retour
          </Button>
        ) : null}
        {/* Terminer enregistre les notes en attente : les perdre à la fermeture
            serait le piège classique d'un formulaire à deux boutons. */}
        <Button type="button" variant="accent" loading={save.isPending} onClick={() => void saveNotes(onDone)}>
          Terminer
        </Button>
      </div>
    </div>
  );
}
